/**
 * Balance Sheet, Profit & Loss, Trial Balance, Ledger, Day Book, Sales
 * Register, Stock Summary, Cash/Bank Book and Search — all derived from the
 * same Snapshot, all pure, none of them needing an extra extraction.
 */
import type { Snapshot, Voucher } from "@tally/shared";

import {
  indexGroups,
  isBank,
  isCash,
  isPayable,
  isReceivable,
  isSalesLedger,
  money,
  sum,
} from "./groups.js";
import type { GroupIndex } from "./groups.js";

/* ------------------------------------------------------------- dashboard --- */

export type Dashboard = {
  cash: number;
  bank: number;
  receivable: number;
  payable: number;
  salesMonthToDate: number;
  salesThisMonthLabel: string;
  workingCapital: number;
  vouchersThisMonth: number;
};

/** Ledger balances are debit-negative; a cash balance in hand reads positive. */
function debitBalance(closing: number): number {
  return money(-closing);
}

export function deriveDashboard(
  snapshot: Snapshot,
  asOf: string,
  index: GroupIndex = indexGroups(snapshot),
): Dashboard {
  const cash = sum(
    snapshot.ledgers.filter((l) => isCash(index, l)).map((l) => debitBalance(l.closingBalance)),
  );
  const bank = sum(
    snapshot.ledgers.filter((l) => isBank(index, l)).map((l) => debitBalance(l.closingBalance)),
  );
  const receivable = sum(
    snapshot.ledgers
      .filter((l) => isReceivable(index, l) && l.closingBalance < 0)
      .map((l) => debitBalance(l.closingBalance)),
  );
  const payable = sum(
    snapshot.ledgers
      .filter((l) => isPayable(index, l) && l.closingBalance > 0)
      .map((l) => l.closingBalance),
  );

  const month = asOf.slice(0, 7);
  const monthVouchers = postedVouchers(snapshot).filter((v) => v.date.startsWith(month));

  // Sales are credits, so they arrive positive already.
  const salesMonthToDate = sum(
    monthVouchers.flatMap((v) =>
      v.ledgerEntries
        .filter((e) => {
          const ledger = snapshot.ledgers.find((l) => l.name === e.ledger);
          return ledger ? isSalesLedger(index, ledger) : false;
        })
        .map((e) => e.amount),
    ),
  );

  return {
    cash,
    bank,
    receivable,
    payable,
    salesMonthToDate,
    salesThisMonthLabel: monthLabel(month),
    workingCapital: money(cash + bank + receivable - payable),
    vouchersThisMonth: monthVouchers.length,
  };
}

export function monthLabel(yyyymm: string): string {
  const [year, month] = yyyymm.split("-");
  const names = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  const idx = Number.parseInt(month ?? "1", 10) - 1;
  return `${names[idx] ?? "?"} ${year ?? ""}`.trim();
}

/** Cancelled and optional vouchers are outside the books, everywhere. */
export function postedVouchers(snapshot: Snapshot): Voucher[] {
  return snapshot.vouchers.filter((v) => !v.isCancelled && !v.isOptional);
}

/* ---------------------------------------------------------------- ledger --- */

export type LedgerLine = {
  voucherId: string;
  date: string;
  voucherType: string;
  voucherNumber: string;
  narration?: string;
  /** signed, debit-negative */
  amount: number;
  /** running balance after this line, debit-negative */
  balance: number;
  counterparty: string;
};

export type LedgerStatement = {
  ledger: string;
  opening: number;
  closing: number;
  computedClosing: number;
  /** true when the running balance lands exactly on the reported closing */
  tiesOut: boolean;
  lines: LedgerLine[];
};

export function deriveLedger(snapshot: Snapshot, ledgerName: string): LedgerStatement | null {
  const ledger = snapshot.ledgers.find((l) => l.name === ledgerName);
  if (!ledger) return null;

  const lines: LedgerLine[] = [];
  let balance = ledger.openingBalance;

  const relevant = postedVouchers(snapshot)
    .filter((v) => v.ledgerEntries.some((e) => e.ledger === ledgerName))
    .sort((a, b) => a.date.localeCompare(b.date) || a.voucherNumber.localeCompare(b.voucherNumber));

  for (const voucher of relevant) {
    for (const entry of voucher.ledgerEntries) {
      if (entry.ledger !== ledgerName) continue;
      balance = money(balance + entry.amount);

      const others = voucher.ledgerEntries
        .filter((e) => e.ledger !== ledgerName)
        .map((e) => e.ledger);

      const line: LedgerLine = {
        voucherId: voucher.id,
        date: voucher.date,
        voucherType: voucher.voucherType,
        voucherNumber: voucher.voucherNumber,
        amount: entry.amount,
        balance,
        counterparty: others.length === 1 ? others[0]! : others.length === 0 ? "" : `${others.length} accounts`,
      };
      if (voucher.narration) line.narration = voucher.narration;
      lines.push(line);
    }
  }

  const computedClosing = money(balance);
  return {
    ledger: ledgerName,
    opening: money(ledger.openingBalance),
    closing: money(ledger.closingBalance),
    computedClosing,
    tiesOut: Math.abs(computedClosing - money(ledger.closingBalance)) < 0.01,
    lines,
  };
}

export type PartyRow = { name: string; group: string; balance: number };

export function deriveLedgerList(
  snapshot: Snapshot,
  index: GroupIndex = indexGroups(snapshot),
): PartyRow[] {
  return snapshot.ledgers
    .map((l) => ({ name: l.name, group: l.parent, balance: money(l.closingBalance) }))
    .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance) || a.name.localeCompare(b.name))
    .map((row) => ({ ...row, group: row.group || index.primaryOf(row.name) }));
}

/* --------------------------------------------------------------- daybook --- */

export type DayBookEntry = {
  id: string;
  date: string;
  voucherType: string;
  voucherNumber: string;
  party: string;
  narration?: string;
  /** the voucher's magnitude: the total of its debits */
  amount: number;
};

export function deriveDayBook(snapshot: Snapshot, filter?: { from?: string; to?: string }): DayBookEntry[] {
  return postedVouchers(snapshot)
    .filter((v) => (!filter?.from || v.date >= filter.from) && (!filter?.to || v.date <= filter.to))
    .map(toDayBookEntry)
    .sort((a, b) => b.date.localeCompare(a.date) || b.voucherNumber.localeCompare(a.voucherNumber));
}

function toDayBookEntry(voucher: Voucher): DayBookEntry {
  // The magnitude of a voucher is the sum of one side, not of both — the two
  // sides cancel by construction.
  const debits = voucher.ledgerEntries.filter((e) => e.amount < 0).map((e) => -e.amount);
  const entry: DayBookEntry = {
    id: voucher.id,
    date: voucher.date,
    voucherType: voucher.voucherType,
    voucherNumber: voucher.voucherNumber,
    party: voucher.partyLedger ?? voucher.ledgerEntries[0]?.ledger ?? "",
    amount: sum(debits),
  };
  if (voucher.narration) entry.narration = voucher.narration;
  return entry;
}

/* -------------------------------------------------------- sales register --- */

export type MonthTotal = { month: string; label: string; amount: number; count: number };

export type SalesRegister = {
  entries: DayBookEntry[];
  months: MonthTotal[];
  total: number;
};

export function deriveSalesRegister(
  snapshot: Snapshot,
  index: GroupIndex = indexGroups(snapshot),
  filter?: { from?: string; to?: string },
): SalesRegister {
  const salesLedgers = new Set(
    snapshot.ledgers.filter((l) => isSalesLedger(index, l)).map((l) => l.name),
  );

  const rows: DayBookEntry[] = [];
  for (const voucher of postedVouchers(snapshot)) {
    if (filter?.from && voucher.date < filter.from) continue;
    if (filter?.to && voucher.date > filter.to) continue;

    const revenue = voucher.ledgerEntries
      .filter((e) => salesLedgers.has(e.ledger))
      .reduce((total, e) => total + e.amount, 0);
    if (Math.abs(revenue) < 0.01) continue;

    const entry: DayBookEntry = {
      id: voucher.id,
      date: voucher.date,
      voucherType: voucher.voucherType,
      voucherNumber: voucher.voucherNumber,
      party: voucher.partyLedger ?? "",
      amount: money(revenue),
    };
    if (voucher.narration) entry.narration = voucher.narration;
    rows.push(entry);
  }

  const byMonth = new Map<string, MonthTotal>();
  for (const row of rows) {
    const month = row.date.slice(0, 7);
    const existing = byMonth.get(month);
    if (existing) {
      existing.amount = money(existing.amount + row.amount);
      existing.count++;
    } else {
      byMonth.set(month, { month, label: monthLabel(month), amount: row.amount, count: 1 });
    }
  }

  return {
    entries: rows.sort((a, b) => b.date.localeCompare(a.date)),
    months: [...byMonth.values()].sort((a, b) => b.month.localeCompare(a.month)),
    total: sum(rows.map((r) => r.amount)),
  };
}

/* --------------------------------------------------------------- stock --- */

export type StockRow = {
  name: string;
  group: string;
  unit: string;
  qty: number;
  value: number;
  rate: number;
};

export function deriveStockSummary(snapshot: Snapshot): { rows: StockRow[]; totalValue: number } {
  const rows: StockRow[] = snapshot.stockItems
    .map((item) => ({
      name: item.name,
      group: item.parent,
      unit: item.baseUnit,
      qty: item.closingQty,
      value: money(item.closingValue),
      rate: item.closingQty === 0 ? 0 : money(item.closingValue / item.closingQty),
    }))
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value) || a.name.localeCompare(b.name));

  return { rows, totalValue: sum(rows.map((r) => r.value)) };
}

/* --------------------------------------------------------------- search --- */

export type SearchHit =
  | { kind: "ledger"; label: string; sub: string; balance: number }
  | { kind: "item"; label: string; sub: string; balance: number }
  | { kind: "voucher"; label: string; sub: string; balance: number; date: string; id: string };

export function search(snapshot: Snapshot, query: string, limit = 40): SearchHit[] {
  const needle = query.trim().toLowerCase();
  if (needle.length < 2) return [];

  const hits: SearchHit[] = [];

  for (const ledger of snapshot.ledgers) {
    if (ledger.name.toLowerCase().includes(needle)) {
      hits.push({
        kind: "ledger",
        label: ledger.name,
        sub: ledger.parent,
        balance: money(ledger.closingBalance),
      });
    }
  }

  for (const item of snapshot.stockItems) {
    if (item.name.toLowerCase().includes(needle) || item.hsnCode?.includes(needle)) {
      hits.push({
        kind: "item",
        label: item.name,
        sub: `${item.closingQty} ${item.baseUnit}`,
        balance: money(item.closingValue),
      });
    }
  }

  // Newest first — a voucher number search that returns 2024 before 2026 makes
  // the reader scroll to find the one they almost certainly meant.
  const voucherHits: SearchHit[] = [];
  for (const voucher of postedVouchers(snapshot)) {
    const matches =
      voucher.voucherNumber.toLowerCase().includes(needle) ||
      (voucher.narration?.toLowerCase().includes(needle) ?? false) ||
      (voucher.reference?.toLowerCase().includes(needle) ?? false);
    if (!matches) continue;

    const entry = toDayBookEntry(voucher);
    voucherHits.push({
      kind: "voucher",
      label: `${voucher.voucherType} ${voucher.voucherNumber}`,
      sub: voucher.partyLedger ?? voucher.narration ?? "",
      balance: entry.amount,
      date: voucher.date,
      id: voucher.id,
    });
  }
  voucherHits.sort((a, b) =>
    a.kind === "voucher" && b.kind === "voucher" ? b.date.localeCompare(a.date) : 0,
  );
  hits.push(...voucherHits);

  return hits.slice(0, limit);
}

/* -------------------------------------------- balance sheet / P&L / TB --- */

export type ReportLine = { name: string; amount: number; children?: ReportLine[] };

export type TrialBalanceRow = { ledger: string; group: string; debit: number; credit: number };

export type TrialBalance = {
  rows: TrialBalanceRow[];
  totalDebit: number;
  totalCredit: number;
  difference: number;
};

export function deriveTrialBalance(
  snapshot: Snapshot,
  index: GroupIndex = indexGroups(snapshot),
): TrialBalance {
  const rows: TrialBalanceRow[] = snapshot.ledgers
    .filter((l) => Math.abs(l.closingBalance) >= 0.01)
    .map((l) => ({
      ledger: l.name,
      group: l.parent || index.primaryOf(l.name),
      debit: l.closingBalance < 0 ? money(-l.closingBalance) : 0,
      credit: l.closingBalance > 0 ? money(l.closingBalance) : 0,
    }))
    .sort((a, b) => a.group.localeCompare(b.group) || a.ledger.localeCompare(b.ledger));

  const totalDebit = sum(rows.map((r) => r.debit));
  const totalCredit = sum(rows.map((r) => r.credit));

  return { rows, totalDebit, totalCredit, difference: money(totalDebit - totalCredit) };
}

export type ProfitLoss = {
  income: ReportLine[];
  expenses: ReportLine[];
  totalIncome: number;
  totalExpenses: number;
  grossProfit: number;
  netProfit: number;
};

export function deriveProfitLoss(
  snapshot: Snapshot,
  index: GroupIndex = indexGroups(snapshot),
): ProfitLoss {
  const revenue = snapshot.ledgers.filter((l) => index.isRevenue(l.name));

  // Income is credit (positive), expense is debit (negative). Both are shown as
  // positive magnitudes; the sign carries the meaning, not the display.
  const incomeGroups = groupBy(revenue.filter((l) => l.closingBalance > 0), (l) =>
    l.parent || index.primaryOf(l.name),
  );
  const expenseGroups = groupBy(revenue.filter((l) => l.closingBalance < 0), (l) =>
    l.parent || index.primaryOf(l.name),
  );

  const income = toLines(incomeGroups, (l) => money(l.closingBalance));
  const expenses = toLines(expenseGroups, (l) => money(-l.closingBalance));

  const totalIncome = sum(income.map((l) => l.amount));
  const totalExpenses = sum(expenses.map((l) => l.amount));

  // Gross profit uses only the groups Tally marks as affecting it (trading
  // account); everything else falls below the line.
  const grossIncome = sum(
    revenue
      .filter((l) => l.closingBalance > 0 && affectsGrossProfit(index, l.parent))
      .map((l) => money(l.closingBalance)),
  );
  const grossExpense = sum(
    revenue
      .filter((l) => l.closingBalance < 0 && affectsGrossProfit(index, l.parent))
      .map((l) => money(-l.closingBalance)),
  );

  return {
    income,
    expenses,
    totalIncome,
    totalExpenses,
    grossProfit: money(grossIncome - grossExpense),
    netProfit: money(totalIncome - totalExpenses),
  };
}

function affectsGrossProfit(index: GroupIndex, groupName: string): boolean {
  const group = index.byName.get(groupName.toLowerCase());
  return group?.affectsGrossProfit ?? false;
}

export type BalanceSheet = {
  assets: ReportLine[];
  liabilities: ReportLine[];
  totalAssets: number;
  totalLiabilities: number;
  netProfit: number;
  difference: number;
};

export function deriveBalanceSheet(
  snapshot: Snapshot,
  index: GroupIndex = indexGroups(snapshot),
): BalanceSheet {
  const capital = snapshot.ledgers.filter((l) => !index.isRevenue(l.name));

  const assetLedgers = capital.filter((l) => l.closingBalance < 0);
  const liabilityLedgers = capital.filter((l) => l.closingBalance > 0);

  const assets = toLines(
    groupBy(assetLedgers, (l) => l.parent || index.primaryOf(l.name)),
    (l) => money(-l.closingBalance),
  );
  const liabilities = toLines(
    groupBy(liabilityLedgers, (l) => l.parent || index.primaryOf(l.name)),
    (l) => money(l.closingBalance),
  );

  const totalAssets = sum(assets.map((l) => l.amount));
  const totalLiabilities = sum(liabilities.map((l) => l.amount));

  // The profit for the period is what closes the two sides. Showing the gap
  // without naming it is how a balance sheet ends up looking broken when it
  // is not.
  const netProfit = deriveProfitLoss(snapshot, index).netProfit;

  return {
    assets,
    liabilities,
    totalAssets,
    totalLiabilities,
    netProfit,
    difference: money(totalAssets - totalLiabilities - netProfit),
  };
}

function groupBy<T>(items: T[], key: (item: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const k = key(item) || "Ungrouped";
    const bucket = map.get(k);
    if (bucket) bucket.push(item);
    else map.set(k, [item]);
  }
  return map;
}

function toLines<T extends { name: string }>(
  groups: Map<string, T[]>,
  amountOf: (item: T) => number,
): ReportLine[] {
  return [...groups.entries()]
    .map(([name, items]) => ({
      name,
      amount: sum(items.map(amountOf)),
      children: items
        .map((item) => ({ name: item.name, amount: amountOf(item) }))
        .sort((a, b) => b.amount - a.amount),
    }))
    .sort((a, b) => b.amount - a.amount);
}

/* ----------------------------------------------------------- cash book --- */

export type CashBookAccount = { ledger: string; kind: "cash" | "bank"; balance: number };

export function deriveCashAccounts(
  snapshot: Snapshot,
  index: GroupIndex = indexGroups(snapshot),
): CashBookAccount[] {
  return snapshot.ledgers
    .filter((l) => isCash(index, l) || isBank(index, l))
    .map((l) => ({
      ledger: l.name,
      kind: isCash(index, l) ? ("cash" as const) : ("bank" as const),
      balance: debitBalance(l.closingBalance),
    }))
    .sort((a, b) => Math.abs(b.balance) - Math.abs(a.balance));
}

/* -------------------------------------------------------------- ratios --- */

export type Ratio = {
  name: string;
  value: number | null;
  display: string;
  hint: string;
};

export function deriveRatios(
  snapshot: Snapshot,
  asOf: string,
  index: GroupIndex = indexGroups(snapshot),
): Ratio[] {
  const dashboard = deriveDashboard(snapshot, asOf, index);
  const pnl = deriveProfitLoss(snapshot, index);

  const inventory = sum(snapshot.stockItems.map((i) => i.closingValue));
  const currentAssets = money(dashboard.cash + dashboard.bank + dashboard.receivable + inventory);
  const currentLiabilities = dashboard.payable;

  const equity = sum(
    snapshot.ledgers
      .filter((l) => index.under(l.name, "capital account"))
      .map((l) => money(l.closingBalance)),
  );

  const ratios: Ratio[] = [
    ratio("Current ratio", currentAssets, currentLiabilities, "x", "current assets ÷ current liabilities"),
    ratio(
      "Quick ratio",
      money(currentAssets - inventory),
      currentLiabilities,
      "x",
      "excludes inventory",
    ),
    {
      name: "Working capital",
      value: money(currentAssets - currentLiabilities),
      display: formatMoney(money(currentAssets - currentLiabilities)),
      hint: "current assets − current liabilities",
    },
    ratio("Debt to equity", currentLiabilities, equity, "x", "current liabilities ÷ capital"),
    percent("Gross profit", pnl.grossProfit, pnl.totalIncome, "gross profit ÷ income"),
    percent("Net profit", pnl.netProfit, pnl.totalIncome, "net profit ÷ income"),
    percent(
      "Receivables vs sales",
      dashboard.receivable,
      pnl.totalIncome,
      "receivables ÷ income for the period",
    ),
  ];

  return ratios;
}

function ratio(name: string, numerator: number, denominator: number, unit: string, hint: string): Ratio {
  if (Math.abs(denominator) < 0.01) {
    // A ratio over zero is undefined, not infinite and not zero. Saying so beats
    // printing "Infinity" or a confident 0.00.
    return { name, value: null, display: "—", hint };
  }
  const value = money(numerator / denominator);
  return { name, value, display: `${value.toFixed(2)}${unit}`, hint };
}

function percent(name: string, numerator: number, denominator: number, hint: string): Ratio {
  if (Math.abs(denominator) < 0.01) return { name, value: null, display: "—", hint };
  const value = money((numerator / denominator) * 100);
  return { name, value, display: `${value.toFixed(1)}%`, hint };
}

/* ------------------------------------------------------------ formatting --- */

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const inrExact = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatMoney(amount: number): string {
  return inr.format(amount);
}

export function formatMoneyExact(amount: number): string {
  return inrExact.format(amount);
}

export function formatQty(qty: number, unit: string): string {
  const rounded = Math.round(qty * 1000) / 1000;
  return unit ? `${rounded} ${unit}` : String(rounded);
}

export function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const idx = Number.parseInt(m ?? "1", 10) - 1;
  return `${d} ${names[idx] ?? ""} ${y}`;
}
